/**
 * Partner Quotes API Helper
 *
 * Functions for interacting with the Partner Quotes API
 */

import axios from 'axios';

const API_BASE = '/api/partner-quotes';

/**
 * Get list of available partners
 */
export const getAvailablePartners = async () => {
  const response = await axios.get(`${API_BASE}/partners`);
  return response.data;
};

/**
 * Get quote from all partners or specific partner
 *
 * @param {Object} quoteRequest - Quote request parameters
 * @param {string} quoteRequest.garmentSKU - Garment SKU
 * @param {number} quoteRequest.quantity - Quantity
 * @param {Object} quoteRequest.decorationSpecs - Decoration specifications
 * @param {Object} quoteRequest.garmentDetails - Garment details
 * @param {Object} options - Request options
 * @param {string} options.partner - Specific partner ID (optional)
 * @param {boolean} options.parallel - Fetch in parallel (default: true)
 */
export const getQuotes = async (quoteRequest, options = {}) => {
  const { partner, parallel = true } = options;

  const params = new URLSearchParams();
  if (partner) params.append('partner', partner);
  params.append('parallel', parallel.toString());

  const response = await axios.post(
    `${API_BASE}/quote?${params.toString()}`,
    quoteRequest
  );

  return response.data;
};

/**
 * Compare quotes from all partners
 *
 * @param {Object} quoteRequest - Quote request parameters
 */
export const compareQuotes = async (quoteRequest) => {
  const response = await axios.post(`${API_BASE}/compare`, quoteRequest);
  return response.data;
};

/**
 * Get capabilities of a specific partner
 *
 * @param {string} partnerId - Partner ID
 */
export const getPartnerCapabilities = async (partnerId) => {
  const response = await axios.get(`${API_BASE}/partner/${partnerId}/capabilities`);
  return response.data;
};

/**
 * Validate a quote request
 *
 * @param {Object} quoteRequest - Quote request parameters
 * @param {string} partner - Optional specific partner to validate for
 */
export const validateQuoteRequest = async (quoteRequest, partner = null) => {
  const params = new URLSearchParams();
  if (partner) params.append('partner', partner);

  const response = await axios.post(
    `${API_BASE}/validate?${params.toString()}`,
    quoteRequest
  );

  return response.data;
};

/**
 * Save partner quotes to a look configuration
 *
 * @param {string} brandId - Brand ID
 * @param {string} lookId - Look ID
 * @param {Array} quotes - Array of partner quotes
 * @param {string} selectedPartnerId - ID of selected partner
 */
export const saveQuotesToLook = async (brandId, lookId, quotes, selectedPartnerId) => {
  const response = await axios.patch(`/api/brands/${brandId}/looks/${lookId}/quotes`, {
    partnerQuotes: quotes.map(q => ({
      partnerId: q.partnerId,
      partnerName: q.partnerName,
      decorationCost: q.quote.decorationCost,
      setupFee: q.quote.setupFee || 0,
      shippingEstimate: q.quote.shippingEstimate || 0,
      taxEstimate: q.quote.taxEstimate || 0,
      totalCost: q.quote.totalCost,
      leadTimeDays: q.quote.leadTimeDays,
      breakdown: q.quote.breakdown,
      metadata: q.quote.metadata,
      quotedAt: q.quote.quotedAt || new Date().toISOString(),
      expiresAt: q.quote.expiresAt || new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
      score: q.score,
      recommended: q.recommended,
    })),
    selectedPartnerQuote: selectedPartnerId ? {
      partnerId: selectedPartnerId,
      quotedAt: new Date().toISOString(),
    } : null,
  });

  return response.data;
};

export default {
  getAvailablePartners,
  getQuotes,
  compareQuotes,
  getPartnerCapabilities,
  validateQuoteRequest,
  saveQuotesToLook,
};
